import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowRightIcon,
  CheckIcon,
  PencilSimpleIcon,
  ShieldCheckIcon,
  SparkleIcon,
  UserIcon,
} from '@phosphor-icons/react';
import { ApiError, Button, StatusChip, cn } from '@smena/ui';
import { patchSettings } from '@/features/account/api';
import { fetchAutonomyReadiness } from './api';

/**
 * Экран «Финальные рубильники» - на деле объяснение, как вообще пользоваться решением.
 *
 * Раньше здесь были две кнопки («включить автоответы» / «оставить draft») и абзац со ссылкой
 * на PRODUCT §8. Человек в этот момент впервые слышит слово draft и не понимает ни что это,
 * ни что от него потребуется завтра, ни когда это закончится. Кнопка «включить автоответы»
 * при этом стояла первой - то есть предлагала снять единственную страховку раньше, чем
 * объяснила, зачем она.
 *
 * Поэтому: сначала показываем, как выглядит запрос на подтверждение (живой макет, по нему
 * можно нажать), потом говорим, сколько это продлится, и только потом даём рубильник.
 */
export function DraftModeExplainer() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const readinessQ = useQuery({ queryKey: ['autonomy-readiness'], queryFn: fetchAutonomyReadiness });
  const r = readinessQ.data;

  const enableMut = useMutation({
    mutationFn: () => patchSettings({ autoreply_off: false }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings'] });
      void qc.invalidateQueries({ queryKey: ['autonomy-readiness'] });
      toast.success('Автоответы включены - агент отвечает гостям сам');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось переключить'),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-2 rounded-md border border-accent/30 bg-accent/8 p-3 text-sm">
        <ShieldCheckIcon weight="fill" className="mt-0.5 size-4 shrink-0 text-accent-fg" />
        <div>
          <div className="font-semibold">Первое время агент ничего не отправляет сам</div>
          <p className="mt-1 text-xs text-muted">
            Он разбирает вопрос гостя, готовит ответ и показывает его вам. Вы читаете и
            нажимаете «Отправить» - или правите текст, если он вам не нравится. Гость получает
            ответ только после вашего нажатия.
          </p>
          <p className="mt-1 text-xs text-muted">
            Искать эти запросы не нужно: агент сам напишет вам в Telegram или MAX, а если
            установлено приложение - придёт push.
          </p>
        </div>
      </div>

      <div>
        <div className="mb-2 text-sm font-semibold">Выглядеть это будет так</div>
        <DraftDemo />
      </div>

      <div className="rounded-md border border-line bg-panel2/40 p-3">
        <div className="text-sm font-semibold">Сколько так продолжается</div>
        <p className="mt-1 text-xs text-muted">
          По нашему опыту хватает пары-тройки дней: за это время проходит полный цикл хотя бы
          одной брони - заезд, проживание, выезд - и вы видите агента на всех типах вопросов,
          а не только на «во сколько заезд». Дальше почти все переключают его на самостоятельные
          ответы.
        </p>
        <p className="mt-1 text-xs text-muted">
          Следить за календарём не нужно - мы напомним сами, когда увидим, что вы достаточно
          его проверили.
        </p>
      </div>

      {r?.ready ? (
        <ReadyBanner
          days={r.daysObserved}
          confirmed={r.confirmedCount}
          onEnable={() => enableMut.mutate()}
          pending={enableMut.isPending}
          onOpenAutonomy={() => navigate('/agent')}
        />
      ) : (
        <div className="flex flex-col gap-2 rounded-md border border-line bg-panel2/40 p-3">
          <div className="text-sm font-semibold">Сейчас включён режим подтверждения</div>
          <p className="text-xs text-muted">
            {r?.firstReplyAt
              ? `Вы наблюдаете за агентом ${r.daysObserved} ${plural(r.daysObserved, 'день', 'дня', 'дней')}, подтверждено ответов: ${r.confirmedCount}. Напомним о переключении, когда наберётся ${r.minDays} ${plural(r.minDays ?? 3, 'день', 'дня', 'дней')} и ${r.minConfirmed} подтверждений.`
              : 'Отсчёт начнётся с первого вопроса гостя.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={enableMut.isPending}
              onClick={() => enableMut.mutate()}
            >
              Всё равно включить автоответы сейчас
            </Button>
          </div>
          <p className="text-[11px] text-muted">
            Переключить можно в любой момент и обратно тоже - раздел «Агент» → «Стоп-краны».
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Живой макет карточки подтверждения. Намеренно не дергает сервер: это объяснение, а не
 * действие - настоящего гостя за ним нет, и отправлять некому. Но нажать кнопку можно,
 * потому что «покажите, как это выглядит» без возможности потрогать не работает.
 */
function DraftDemo() {
  const [state, setState] = useState<'idle' | 'sent' | 'editing'>('idle');
  const [text, setText] = useState('Заезд с 15:00. Ключи в сейфе у двери, код пришлю в день заезда.');

  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-card p-3">
      <div className="flex flex-wrap items-center gap-2">
        <StatusChip tone="accent">
          <SparkleIcon weight="fill" className="mr-1 inline size-3" />
          пример
        </StatusChip>
        <span className="text-xs text-muted">Агент подготовил ответ - нужно ваше подтверждение</span>
      </div>

      <div className="flex items-start gap-2 rounded-md bg-panel2/60 p-2">
        <UserIcon weight="bold" className="mt-0.5 size-4 shrink-0 text-muted" />
        <div className="text-sm">
          <div className="text-[11px] text-muted">Гость, Анна · Студия на Мира</div>
          Здравствуйте, во сколько можно заехать и как забрать ключи
        </div>
      </div>

      {state === 'editing' ? (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          className="w-full rounded-md border border-line-control bg-input px-3 py-2 text-sm text-text"
        />
      ) : (
        <div
          className={cn(
            'rounded-md border p-2 text-sm',
            state === 'sent' ? 'border-green/40 bg-green/10' : 'border-line bg-panel2/40',
          )}
        >
          {text}
        </div>
      )}

      {state === 'sent' ? (
        <div className="flex items-center gap-2 text-xs text-green">
          <CheckIcon weight="bold" className="size-4" />
          Так это и работает: одно нажатие - и гость получает ответ. Это был пример, никому
          ничего не ушло.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setState('sent')}>
            <CheckIcon weight="bold" className="size-4" /> Отправить гостю
          </Button>
          {state === 'idle' ? (
            <Button size="sm" variant="secondary" onClick={() => setState('editing')}>
              <PencilSimpleIcon weight="bold" className="size-4" /> Поправить текст
            </Button>
          ) : null}
        </div>
      )}

      {state === 'sent' ? (
        <Button
          size="sm"
          variant="ghost"
          className="self-start"
          onClick={() => {
            setState('idle');
            setText('Заезд с 15:00. Ключи в сейфе у двери, код пришлю в день заезда.');
          }}
        >
          Показать ещё раз
        </Button>
      ) : null}
    </div>
  );
}

export function ReadyBanner({
  days,
  confirmed,
  onEnable,
  pending,
  onOpenAutonomy,
}: {
  days: number;
  confirmed: number;
  onEnable: () => void;
  pending: boolean;
  onOpenAutonomy: () => void;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-md border border-green/40 bg-green/10 p-3">
      <div className="text-sm font-semibold text-green">Пора отпускать агента к гостям</div>
      <p className="text-xs text-muted">
        Вы проверяли его {days} {plural(days, 'день', 'дня', 'дней')} и подтвердили {confirmed}{' '}
        {plural(confirmed, 'ответ', 'ответа', 'ответов')}. Этого достаточно, чтобы понять, что
        он отвечает как надо. Включите автоответы - агент начнёт отвечать гостям сразу, а к вам
        будет приходить только то, что решать должен человек: деньги, жалобы, нестандартные
        просьбы.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={pending} onClick={onEnable}>
          {pending ? 'Включаем…' : 'Включить автоответы'}
          <ArrowRightIcon weight="bold" className="size-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onOpenAutonomy}>
          Сначала посмотреть настройки
        </Button>
      </div>
    </div>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}
